<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    class="sequencer-modal"
    :style="modal.modalStyle.value"
    @mousedown="bringToFront"
  >
    <!-- Header (draggable) -->
    <div
      ref="headerRef"
      class="modal-header"
      :class="{ flashing: modal.isFlashing.value }"
      @mousedown="onStartDrag"
    >
      <span class="modal-title">Sequencer</span>
      <button class="close-btn" @click="close" title="Close"><XMarkIcon class="close-icon" /></button>
    </div>

    <!-- Content -->
    <div class="modal-content">
      <div class="coming-soon">
        <span class="coming-soon-text">Sequencer data coming soon</span>
      </div>
    </div>

    <!-- Resize Handles -->
    <div class="resize-handle resize-right" @mousedown.stop="(e) => modal.startResize('right', e)"></div>
    <div class="resize-handle resize-bottom" @mousedown.stop="(e) => modal.startResize('bottom', e)"></div>
    <div class="resize-handle resize-corner" @mousedown.stop="(e) => modal.startResize('corner', e)"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from 'vue';
import { XMarkIcon } from '@/components/icons';
import { useModalDragResize } from '@/composables/useModalDragResize';

// Props
export interface SequencerModalProps {
  zIndexBase?: number;
}

const props = withDefaults(defineProps<SequencerModalProps>(), {
  zIndexBase: 1000
});

// Emits
const emit = defineEmits<{
  close: [];
  focus: [];
}>();

// Modal drag/resize composable
const modal = useModalDragResize({
  initialWidth: 280,
  initialHeight: 140,
  minWidth: 200,
  minHeight: 100,
  zIndexBase: props.zIndexBase
});

// State
const isOpen = ref(false);
const headerRef = ref<HTMLElement>();

const onStartDrag = (e: MouseEvent) => {
  modal.startDrag(e, headerRef.value);
};

const open = (mouseX: number, mouseY: number) => {
  modal.positionNear(mouseX, mouseY);
  isOpen.value = true;
  modal.flashHeader();
};

const close = () => {
  isOpen.value = false;
  emit('close');
};

const bringToFront = () => {
  emit('focus');
};

onUnmounted(() => {
  // Cleanup if needed
});

defineExpose({
  open,
  close,
  setZIndex: modal.setZIndex,
  flashHeader: modal.flashHeader,
  isOpen: () => isOpen.value
});
</script>

<style scoped>
.sequencer-modal {
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  font-family: Inter, Arial, sans-serif;
  user-select: none;
  box-shadow: var(--shadow-lg);
  border-radius: 4px;
}

.modal-header {
  height: 40px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  cursor: move;
  flex-shrink: 0;
  transition: background 0.3s;
  position: relative;
  border-radius: 4px 4px 0 0;
}

.modal-header.flashing {
  animation: headerFlash 500ms ease-out;
}

@keyframes headerFlash {
  0% { background: var(--accent-primary); opacity: 0.3; }
  100% { background: var(--bg-elevated); }
}

.modal-title {
  color: var(--accent-primary);
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 1px solid var(--error);
  color: var(--error);
  width: 24px;
  height: 24px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.close-btn:hover {
  background: var(--error);
  color: var(--text-inverse);
}

.close-icon {
  width: 14px;
  height: 14px;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  color: var(--text-primary);
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coming-soon {
  text-align: center;
}

.coming-soon-text {
  color: var(--text-muted);
  font-size: 14px;
  font-style: italic;
}

.resize-handle {
  position: absolute;
  background: transparent;
}

.resize-right {
  right: 0;
  top: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
}

.resize-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
  height: 8px;
  cursor: ns-resize;
}

.resize-corner {
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  background: var(--accent-primary);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.resize-corner:hover {
  background: var(--success);
}
</style>
